// Wind product registry — the 7 Skysight Wind-section products as data.
// Transcribed from docs/skysight-overlay-styles/wind/CAPTURE-NOTES.md.
// DO NOT edit ranges/params without diffing the capture.
//
// kind 'uv'     -> speed contour fill + barbs/streams (vector field, ±16 m/s).
// kind 'scalar' -> contour fill only (single signed field, own range).
// `param: null` on wind-altitude => resolved per selected level via paramForLevel().

// Per-parameter uv dequantization ranges + fill value ranges (uPmin..uPmax),
// captured from Skysight's live contour/barbs uniforms. NOTE: Skysight re-quantizes
// each forecast RUN to its own range, so these drift ±1-2 m/s day-to-day; exact
// match needs Skysight's per-run range metadata (source not yet found). These are
// the best fixed approximation and far closer than a flat ±16. See CAPTURE-NOTES.
const R14 = { min: [-14, -14], max: [14, 14] };
const R17 = { min: [-17, -17], max: [17, 17] };

// Speed-fill palettes captured verbatim from Skysight's wind-composite-contours
// activePaletteKey (programVariant "wind-speed"). Format "<max>:<RRGGBBAA>...".
// max/4 = max m/s. DO NOT edit without diffing the capture.
const PAL_52 = '52:00008fff0017ffff009effff00f4dbff05cb5cff3cc30cffbfec04ffffe600ffffb400ffff6800ffff1100ffb7003bff5e0083ff';
const PAL_64 = '64:00008fff0000fbff0068ffff00d4ffff01ecc1ff05cb5cff22bb0dff8adc07fff3fb01ffffdc00ffffb400ffff7900ffff3400ffec000fffa50049ff5e0083ff';
const PAL_76 = '76:00008fff0000e9ff0044ffff009effff00f8ffff03e7b1ff05cb5cff11b50eff68d009ffbfec04fffff700ffffd500ffffb400ffff8500ffff4b00ffff1100ffd50023ff990053ff5e0083ff';

// Scalar (diverging isoband) palettes, captured verbatim from Skysight's live
// contour uniforms (programVariant "scalar"). 10/18 bands + 1 overflow colour;
// both include a fully TRANSPARENT band straddling 0 (the neutral zone).
const PAL_CONVERGENCE = '44:00008fff0032ffff0032ff0004dc8fff22bb0dffbfec04ffffdc00ffff9c00ffff3400ffc8002dff5e0083ff';
const PAL_RIDGE = '76:00008fff0000d5ff001dffff0063ffff00a9ffff00efffff00efff00e3f702b3ffbe00ffff8500ffff4600ffff0e00ffd9001fffa90046ff7b006bff58007cff450064ff33004eff220038ff';

export const WIND_PRODUCTS = [
  {
    id: 'wind-altitude', label: 'Wind at altitude', kind: 'uv',
    levelStepped: true, param: null, range: R17, fillPaletteKey: PAL_64,
    fillRange: { min: 1, max: 16 }, legend: { unit: 'm/s', max: 16 },
  },
  {
    id: 'surface-wind', label: 'Surface Wind', kind: 'uv',
    param: 'uv_10', range: R14, fillPaletteKey: PAL_52,
    fillRange: { min: 0, max: 12 }, legend: { unit: 'm/s', max: 12 },
  },
  {
    id: 'boundary-layer-wind', label: 'Thermal Layer Wind (Avg)', kind: 'uv',
    param: 'blwind', range: R17, fillPaletteKey: PAL_64,
    fillRange: { min: 1, max: 16 }, legend: { unit: 'm/s', max: 16 },
  },
  {
    id: 'bl-top-wind', label: 'Thermal Top Wind', kind: 'uv',
    param: 'bltopwind', range: R17, fillPaletteKey: PAL_76,
    fillRange: { min: 1, max: 19 }, legend: { unit: 'm/s', max: 19 },
  },
  {
    id: 'valley-winds', label: 'Valley Winds', kind: 'uv',
    param: 'uv_10', valleyMask: true, range: R14, fillPaletteKey: PAL_52,
    fillRange: { min: 0, max: 12 }, legend: { unit: 'm/s', max: 12 },
  },
  // Scalar fills: range = uPmin..uPmax (palette value range — the quantization
  // range too; scalar index in the RED channel, see decode.decodeScalar).
  // maxTileZoom probed live 2026-06-10 by DECODING tiles, not just status codes:
  // wblmaxmin z8 returns 200 but is ~100% sentinel (model res caps at z7);
  // ridge (terrain-derived) carries real data through z10, 404s above.
  {
    id: 'convergence', label: 'Convergence', kind: 'scalar',
    param: 'wblmaxmin', range: { min: -0.25, max: 0.75 }, step: 0.1,
    fillPaletteKey: PAL_CONVERGENCE, maxTileZoom: 7,
    legend: { unit: 'm/s', min: -0.25, max: 0.75 },
  },
  {
    id: 'ridge-lift', label: 'Ridge Lift', kind: 'scalar',
    param: 'ridge', range: { min: -2.6, max: 4.6 }, step: 0.4,
    fillPaletteKey: PAL_RIDGE, maxTileZoom: 10,
    legend: { unit: 'm/s', min: -2.6, max: 4.6 },
  },
];

export const getWindProduct = (id) => WIND_PRODUCTS.find((p) => p.id === id) || null;
